<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <section
    class="integration-email"
    :class="
      variant === 'panel'
        ? 'integration-email--panel'
        : 'flex w-[548px] flex-col items-start gap-4 self-stretch'
    "
  >
    <header v-if="variant === 'panel'" class="integration-email__header">
      <div>
        <UITextSmMedium data-testid="email-panel-heading">
          {{ t("integrate.email.shareViaEmail") }}
        </UITextSmMedium>
      </div>
    </header>
    <div v-else>
      <UITextSmMedium data-testid="email-panel-heading">
        {{ t("common.integratePopup.email.shareViaEmail") }}
      </UITextSmMedium>
      <UITextXsRegular data-testid="email-panel-description">
        {{ emailSubheader }}
      </UITextXsRegular>
    </div>

    <section
      data-testid="email-option-grid"
      :class="
        variant === 'panel'
          ? 'integration-email__options integration-email__options--panel'
          : 'flex items-start gap-4 self-stretch'
      "
    >
      <div
        data-testid="email-blank-template"
        class="card-template cursor-pointer"
        role="button"
        tabindex="0"
        :class="{
          'integration-email__option': variant === 'panel',
          'border-active-blank':
            templateState.type === EMAIL_CAMPAIGN_TYPE.BLANK &&
            !templateState.isDropdownSelected,
        }"
        @click="createBlankTemplate"
        @keydown.enter.prevent="createBlankTemplate"
        @keydown.space.prevent="createBlankTemplate"
      >
        <div
          v-if="variant === 'legacy'"
          class="flex flex-col items-center justify-center gap-[1rem] self-stretch p-3"
        >
          <div
            class="flex h-[120px] w-[220px] items-center justify-center gap-2 self-stretch rounded-lg bg-[#F2F4F7]"
          >
            <PlusIcon class="h-8 w-8" />
          </div>
          <UITextSmMedium>{{
            t("common.integratePopup.email.blankTemplate")
          }}</UITextSmMedium>
        </div>
        <template v-else>
          <div class="integration-email__media">
            <PlusIcon class="integration-email__plus" />
          </div>
          <UITextSmMedium class="integration-email__option-label">
            {{ t("integrate.email.startFromScratch") }}
          </UITextSmMedium>
        </template>
      </div>
      <div
        data-testid="email-marketing-templates"
        class="card-template cursor-pointer"
        role="button"
        tabindex="0"
        :class="{
          'integration-email__option': variant === 'panel',
          'border-active-blank':
            templateState.type === EMAIL_CAMPAIGN_TYPE.MARKETING &&
            !templateState.isDropdownSelected,
        }"
        @click="createEmailMarketingTemplate"
        @keydown.enter.prevent="createEmailMarketingTemplate"
        @keydown.space.prevent="createEmailMarketingTemplate"
      >
        <div
          v-if="variant === 'legacy'"
          class="flex flex-col items-center justify-center gap-[1rem] self-stretch p-3"
        >
          <TemplateIcon
            data-testid="legacy-email-template-collage"
            class="h-[120px] w-[220px]"
            aria-hidden="true"
          />
          <UITextSmMedium>{{
            t("common.integratePopup.email.emailMarketingTemplates")
          }}</UITextSmMedium>
        </div>
        <template v-else>
          <div class="integration-email__media integration-email__media--templates">
            <img
              data-testid="email-templates-card-image"
              class="integration-email__template-image"
              :src="integrateEmailTemplatesImage"
              :alt="t('integrate.email.templateImageAlt')"
            />
          </div>
          <UITextSmMedium class="integration-email__option-label">
            {{ t("integrate.email.emailMarketingTemplates") }}
          </UITextSmMedium>
        </template>
      </div>
    </section>

    <section
      v-if="variant === 'legacy'"
      class="flex flex-col items-start gap-4 self-stretch"
    >
      <div class="flex items-center gap-2 self-stretch">
        <div class="h-[1px] flex-1 bg-gray-200 text-gray-200"></div>
        <UITextSmMedium>{{ t("common.integratePopup.email.or") }}</UITextSmMedium>
        <div class="h-[1px] flex-1 bg-gray-200 text-gray-200"></div>
      </div>
    </section>
    <div v-else class="integration-email__divider">
      <span></span>
      <UITextSmMedium>{{ t("common.integratePopup.email.or") }}</UITextSmMedium>
      <span></span>
    </div>

    <section
      :class="
        variant === 'panel'
          ? 'integration-email__template-field'
          : 'flex w-full items-start self-stretch'
      "
    >
      <UITextSmMedium v-if="variant === 'panel'">
        {{ t("integrate.email.yourTemplates") }}
      </UITextSmMedium>
      <UISelect
        id="template-select"
        :value="selectedTemplate"
        :filterable="true"
        class="select-placeholder"
        :class="{ 'integration-email__select': variant === 'panel' }"
        :options="templateOptions"
        :placeholder="
          variant === 'panel'
            ? t('integrate.email.selectTemplate')
            : t('common.integratePopup.email.chooseTemplate')
        "
        @change="handleTemplateChange"
      />
    </section>

    <section
      v-if="variant !== 'panel' || templateState.type"
      :class="
        variant === 'panel'
          ? 'integration-email__continue integration-email__continue--panel'
          : 'mt-[238px] flex justify-end self-stretch'
      "
    >
      <UIButton
        id="emailRedirect"
        type="primary"
        :size="variant === 'panel' ? 'small' : 'medium'"
        :disabled="!templateState.type"
        :loading="loading"
        @click="redirectFromContinue"
        >{{ t("common.integratePopup.email.continue") }}</UIButton
      >
    </section>
  </section>
</template>

<script setup lang="ts">
import TemplateIcon from "@/assets/icons/email-template.svg";
import integrateEmailTemplatesImage from "@/assets/img/integrate-email-templates@2x.png";
import config from "@/config";
import { EmailService } from "@/service/EmailServices";
import { useAppStore } from "@/store/app";
import { openLinkInNewTab } from "@/util/methods";
import { PlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UISelect, UITextSmMedium, UITextXsRegular } from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { EMAIL_CAMPAIGN_TYPE } from "../../../util/constants";

const store = useAppStore();
const { t } = useI18n();

const props = defineProps({
  widgetId: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: "legacy",
    validator: (value: string) => ["legacy", "panel"].includes(value),
  },
});
const templateOptions = ref([]);
const selectedTemplate = ref();
const loading = ref(false);

// Replace individual refs with a single reactive state object
const templateState = ref({
  type: "", // 'blank' or 'marketing' or ''
  campaignData: null,
  isDropdownSelected: false,
});

getEmail();

const emailSubheader = computed(() => {
  if (store.formId) return t("form.integratePopup.email.emailSubheader");
  if (store.surveyId) return t("survey.integratePopup.email.emailSubheader");
  return t("quiz.integratePopup.email.emailSubheader");
});
async function getEmail() {
  const response = await EmailService.getEmailList({
    locationId: store.locationId,
  });
  if (response.data.length) {
    templateOptions.value = response.data.map((template) => {
      return {
        label: template.name,
        value: template._id,
      };
    });
  }
}

const handleTemplateChange = async (templateId) => {
  templateState.value = {
    type: EMAIL_CAMPAIGN_TYPE.TEMPLATE,
    campaignData: null,
    isDropdownSelected: !!templateId,
  };
  selectedTemplate.value = templateId;
};

const createBlankTemplate = async () => {
  if (templateState.value.type !== EMAIL_CAMPAIGN_TYPE.BLANK) {
    templateState.value = {
      type: EMAIL_CAMPAIGN_TYPE.BLANK,
      campaignData: null,
      isDropdownSelected: false,
    };
    selectedTemplate.value = null;
  }
};

const createEmailMarketingTemplate = async () => {
  templateState.value = {
    type: EMAIL_CAMPAIGN_TYPE.MARKETING,
    campaignData: { campaignId: EMAIL_CAMPAIGN_TYPE.MARKETING, formId: props.widgetId },
    isDropdownSelected: false,
  };
  selectedTemplate.value = null;
};

const createCampaign = async (type) => {
  loading.value = true;
  try {
    const campaign = await EmailService.createCampaign({
      locationId: store.locationId,
      templateId: selectedTemplate.value ? selectedTemplate.value : "",
      formId: props.widgetId,
    });
    if (campaign.data) {
      templateState.value.type = type;
      templateState.value.campaignData = campaign.data;
    }
    loading.value = false;
  } catch (err) {
    loading.value = false;
    console.error(err);
  }
};

const redirectToEmailMarketing = () => {
  const url = `https://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/v2/location/${store.locationId}/marketing/emails/scheduled?formId=${
    props.widgetId
  }&pageNumber=1`;
  openLinkInNewTab(url);
};

const redirectToEmailBuilder = () => {
  const url = `https://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/location/${store.locationId}/emails/campaigns/create/${
    templateState.value.campaignData.campaignId
  }?activeElement=${templateState.value.campaignData.formId}`;
  openLinkInNewTab(url);
};

const redirectFromContinue = async () => {
  if (
    templateState.value.type === EMAIL_CAMPAIGN_TYPE.BLANK ||
    templateState.value.type === EMAIL_CAMPAIGN_TYPE.TEMPLATE
  ) {
    await createCampaign(templateState.value.type);
    redirectToEmailBuilder();
  }

  if (templateState.value.type === EMAIL_CAMPAIGN_TYPE.MARKETING) {
    return redirectToEmailMarketing();
  }
};
</script>

<style>
.integration-email {
  display: flex;
  width: 548px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}
.integration-email__header,
.integration-email__options,
.integration-email__template-field,
.integration-email__continue {
  width: 100%;
}
.integration-email__options {
  display: flex;
  align-items: stretch;
  gap: 16px;
}
.integration-email__option {
  align-items: center;
  gap: 16px;
  padding: 12px;
}
.card-template {
  display: flex;
  padding: 0px 2px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 12px;
  border: 2px solid transparent;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.card-template:not(.border-active-blank) {
  border-color: #eaecf0;
}

.border-active-blank {
  border-color: #155eef !important;
  background: #f8fafd;
}
</style>

<style scoped>
.integration-email--panel {
  width: 100%;
  box-sizing: border-box;
  align-items: stretch;
  gap: 24px;
}
.integration-email__header {
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.integration-email--panel .integration-email__header :deep(.hl-text-sm-medium) {
  color: var(--gray-900, #101828);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}
.integration-email__options--panel {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.integration-email--panel .integration-email__option {
  min-width: 0;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  box-shadow: none;
  gap: 0;
}
.integration-email__media {
  position: relative;
  display: flex;
  width: 220px;
  height: 120px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  background: #f2f4f7;
}
.integration-email--panel .integration-email__media {
  width: 100%;
  height: auto;
  aspect-ratio: 880 / 512;
  border-radius: 8px 8px 0 0;
  background: var(--gray-50, #f9fafb);
}
.integration-email__plus {
  width: 32px;
  height: 32px;
  color: var(--gray-500, #667085);
}
.integration-email--panel .integration-email__plus {
  width: 28px;
  height: 28px;
}
.integration-email__template-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.integration-email--panel .integration-email__option-label {
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 10px 12px;
  color: var(--gray-700, #344054);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}
.integration-email__divider {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}
.integration-email--panel .integration-email__divider {
  color: var(--gray-500, #667085);
}
.integration-email--panel .integration-email__divider :deep(.hl-text-sm-medium) {
  font-size: 12px;
  line-height: 18px;
}
.integration-email__divider > span:first-child,
.integration-email__divider > span:last-child {
  height: 1px;
  background: var(--gray-200, #eaecf0);
}
.integration-email__template-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.integration-email--panel .integration-email__template-field :deep(.hl-text-sm-medium) {
  color: var(--gray-900, #101828);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.integration-email__select {
  width: 100%;
}
.integration-email__continue {
  display: flex;
  justify-content: flex-end;
  margin-top: 238px;
}
.integration-email__continue--panel {
  margin-top: auto;
}
.select-placeholder :deep(.n-base-selection-overlay__wrapper) {
  color: var(--gray-500, #667085);
}
</style>
